import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Button,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useManagePortfoliosPracticesStyles } from "./ManagePortfoliosPractices.styles";
import { IEditLabelDialogProps } from "./ManagePortfoliosPractices.types";

export const EditLabelDialog = ({
  entityName,
  initialLabel,
  isOpen,
  isSaving,
  onSave,
  onDismiss,
}: IEditLabelDialogProps): JSX.Element => {
  const styles = useManagePortfoliosPracticesStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const [label, setLabel] = useState<string>("");
  const [labelError, setLabelError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setLabel(initialLabel);
      setLabelError("");
    }
  }, [isOpen, initialLabel]);

  const handleSave = async (): Promise<void> => {
    if (!label.trim()) {
      setLabelError("Label is required");
      return;
    }

    setLabelError("");
    try {
      await onSave(label);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save";
      if (errorMessage.includes("409") || errorMessage.includes("already")) {
        setLabelError("This label already exists.");
      } else {
        setLabelError(errorMessage);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, d) => !d.open && onDismiss()}>
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {initialLabel ? `Edit ${entityName}` : `Add ${entityName}`}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label={`Saving ${entityName.toLowerCase()}...`} />
                </div>
              )}
              <Field
                label="Label"
                required
                validationState={labelError ? "error" : "none"}
                validationMessage={labelError}
                className={styles.dialogField}
              >
                <Input
                  value={label}
                  onChange={(_, d) => {
                    setLabel(d.value);
                    if (labelError) setLabelError("");
                  }}
                  placeholder={`${entityName} label`}
                  disabled={isSaving}
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular aria-hidden="true" />}
            onClick={handleSave}
            disabled={isSaving || !label.trim()}
            tooltip="Save"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
